import React from 'react'
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import StoreIcon from '@mui/icons-material/Store'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HomeIcon from '@mui/icons-material/Home'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import { grey } from '@mui/material/colors'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

interface ThemePreviewProps {
  MainColour: string
  MenuTextColour: string
  LogoLargeURL: string
  LogoSmallURL: string
}

interface CaptionProps {
  text: string
}

const Caption: React.FC<CaptionProps> = ({ text }) => {
  return (
    <Typography variant='caption' sx={{ fontStyle: 'italic', opacity: 0.6 }}>
      {text}
    </Typography>
  )
}

const ThemePreview: React.FC<ThemePreviewProps> = ({ MainColour, MenuTextColour, LogoLargeURL, LogoSmallURL }) => {
  const { t } = useTranslation()

  const [tab, setTab] = React.useState(0)
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  const menuItems = [
    { icon: HomeIcon, label: 'Dashboard.PageTitle' },
    { icon: CalendarTodayIcon, label: 'Calendar.Title' },
    { icon: DirectionsCarIcon, label: 'vehicle.title' }
  ]

  const previewTheme = createTheme({
    palette: {
      primary: {
        main: MainColour
      },
      muted: {
        main: grey[400],
        dark: grey[600],
        light: grey[400],
        contrastText: '#333',
        backgroundHighlight: grey[200]
      },
      LKQGrey: {
        background: { modest: grey[600], highlight: grey[700], surface: grey[100], surfaceHover: grey[50] },
        border: { modest: 'rgba(0, 0, 0, 0.12)' }
      },
      primaryThemeColor: {
        main: MainColour,
        light: 'rgb(66, 135, 255)',
        dark: 'rgb(0, 31, 84)',
        extraDark: 'rgb(0, 19, 53)',
        contrastText: '#fff'
      }
    }
  })

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>
        {t('theme.preview_title')}
      </Typography>
      <Grid container spacing={2}>
        <ThemeProvider theme={previewTheme}>
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: MenuTextColour,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                pointerEvents: 'none',
                height: 53
              }}
            >
              <TextField
                variant='outlined'
                placeholder='Search on ...'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  )
                }}
                sx={{ backgroundColor: 'white', borderRadius: 1, width: '20em', '& input': { p: 1 }, '& fieldset': { border: 0 } }}
              />
              <Stack spacing={2} direction='row'>
                <Box sx={{ color: MenuTextColour, display: 'flex', alignItems: 'center' }}>
                  <StoreIcon sx={{ mr: 0.5 }} />
                  {t('theme.preview_topbar_shop')}
                  <KeyboardArrowDownIcon fontSize='small' sx={{ ml: 1 }} />
                </Box>
                <Box sx={{ color: MenuTextColour, display: 'flex', alignItems: 'center' }}>
                  <AccountCircleIcon sx={{ mr: 0.5 }} />
                  {t('theme.preview_topbar_user')}
                  <KeyboardArrowDownIcon fontSize='small' sx={{ ml: 1 }} />
                </Box>
                <Box sx={{ color: MenuTextColour, display: 'flex', alignItems: 'center' }}>
                  <HelpOutlineIcon sx={{ mr: 0.5 }} />
                  {t('Contact.TopBarTitle')}
                </Box>
              </Stack>
            </Box>
            <Caption text={t('theme.preview_caption_topbar')} />
          </Grid>

          <Grid item xs={6}>
            <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', pointerEvents: 'none', backgroundColor: grey[200] }}>
                <Box sx={{ borderRight: '1px solid rgba(0,0,0,0.2)', minWidth: 220, width: 220, backgroundColor: 'white' }}>
                  <Box sx={{ height: 60, py: 1, px: 2, '& img': { width: '100%', height: 45, objectFit: 'contain' } }}>
                    {LogoLargeURL && <img src={LogoLargeURL} alt='large' />}
                    {!LogoLargeURL && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          border: `2px dashed ${grey[400]}`,
                          color: grey[400]
                        }}
                      >
                        {t('theme.preview_caption_logoLarge')}
                      </Box>
                    )}
                  </Box>

                  <List>
                    {menuItems.map((item, index) => {
                      return (
                        <ListItem key={`listItem-large-${index}`}>
                          <ListItemIcon>{React.createElement(item.icon)}</ListItemIcon>
                          <ListItemText primary={t(item.label)} />
                        </ListItem>
                      )
                    })}
                  </List>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    color: MenuTextColour,
                    display: 'flex',
                    flex: '1 1 auto',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    height: 53,
                    pointerEvents: 'none',
                    alignSelf: 'baseline',
                    position: 'relative'
                  }}
                >
                  <TextField
                    variant='outlined'
                    placeholder='Search on ...'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon fontSize='small' />
                        </InputAdornment>
                      )
                    }}
                    sx={{ backgroundColor: 'white', borderRadius: 1, width: '20em', '& input': { p: 1 }, '& fieldset': { border: 0 } }}
                  />
                </Box>
              </Box>
            </Paper>
            <Caption text={t('theme.preview_caption_logoLarge')} />
          </Grid>
          <Grid item xs={6}>
            <Paper variant='outlined' sx={{ overflowX: 'hidden' }}>
              <Box sx={{ display: 'flex', pointerEvents: 'none', backgroundColor: grey[200] }}>
                <Box sx={{ borderRight: '1px solid rgba(0,0,0,0.2)', width: 60, backgroundColor: 'white' }}>
                  <Box sx={{ height: 60, p: 1, '& img': { width: '100%', height: 45, objectFit: 'contain' } }}>
                    {LogoSmallURL && <img src={LogoSmallURL} alt='large' />}

                    {!LogoSmallURL && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          border: `2px dashed ${grey[400]}`,
                          color: grey[400],
                          fontSize: 12,
                          textAlign: 'center'
                        }}
                      >
                        {t('theme.preview_caption_logoSmall')}
                      </Box>
                    )}
                  </Box>

                  <List sx={{ ml: 0.4 }}>
                    {menuItems.map((item, index) => {
                      return (
                        <ListItem key={`listItem-large-${index}`}>
                          <ListItemIcon>{React.createElement(item.icon)}</ListItemIcon>
                          <ListItemText primary={t(item.label)} sx={{ opacity: 0 }} />
                        </ListItem>
                      )
                    })}
                  </List>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    color: MenuTextColour,
                    display: 'flex',
                    flex: '1 1 auto',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    height: 53,
                    pointerEvents: 'none',
                    alignSelf: 'baseline',
                    position: 'relative'
                  }}
                >
                  <TextField
                    variant='outlined'
                    placeholder='Search on ...'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon fontSize='small' />
                        </InputAdornment>
                      )
                    }}
                    sx={{ backgroundColor: 'white', borderRadius: 1, width: '20em', '& input': { p: 1 }, '& fieldset': { border: 0 } }}
                  />
                </Box>
              </Box>
            </Paper>
            <Caption text={t('theme.preview_caption_logoSmall')} />
          </Grid>

          <Grid item xs={8}>
            <Paper variant='outlined' sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Stack direction='row' spacing={2}>
                <Button>Text Button</Button>
                <Button variant='outlined'>Outlined Button</Button>
                <Button variant='contained'>Contained Button</Button>
              </Stack>
            </Paper>
            <Caption text='Buttons' />
          </Grid>
          <Grid item xs={4}>
            <Paper variant='outlined' sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Stack direction='row' spacing={2}>
                <HomeIcon color='primary' />
                <CalendarTodayIcon color='primary' />
                <DirectionsCarIcon color='primary' />
                <SearchIcon color='primary' />
                <HelpOutlineIcon color='primary' />
              </Stack>
            </Paper>
            <Caption text={t('theme.preview_caption_icons')} />
          </Grid>
          <Grid item xs={12}>
            <Paper variant='outlined' sx={{ pb: 1 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={handleTabChange} aria-label='basic tabs example'>
                  {menuItems.map((item, index) => {
                    return <Tab key={`previewTab-${index}`} label={t(item.label)} />
                  })}
                </Tabs>
              </Box>
            </Paper>
            <Caption text={t('theme.preview_caption_tabs')} />
          </Grid>
        </ThemeProvider>
      </Grid>
    </>
  )
}

export default ThemePreview
